import React, { useEffect, useState } from 'react';
import GrowthShell from '../../components/growth/GrowthShell';
import { fmtAt } from '../../lib/growthRange';
import {
  KIND_AR, PROPOSAL_STATUS_AR, TABS, decideProposal, decisionMessage, expiryText, fetchProposal, fetchProposals, fetchSummary,
  keepProposal, measureManual, resultText,
} from '../../lib/growthApprovals';

// Growth OS «مركز الموافقات» — Growth Director Phase B (owner, 2026-11-03).
// Everything the director proposes waits here (and on WhatsApp) for his
// decision. Approve; keep («احفظه» — no 14-day expiry); reject or ask for
// changes, both with a reason. Approving a decision writes it into «القرارات»
// in the database, not here. The card is shared with the director chat.

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const STATUS_PILL = { pending: 'neutral', approved: 'good', rejected: 'bad', changes_requested: 'bad', superseded: 'low', expired: 'low' };

function Row({ label, children }) {
  if (children == null || children === '') return null;
  return <p style={{ margin: '4px 0' }}><span className="gos-faint">{label}:</span> <span dir="auto">{children}</span></p>;
}

export function ProposalCard({ p: initial, id, compact = false, onChanged }) {
  const [p, setP] = useState(initial || null);
  const [asking, setAsking] = useState(null);   // 'reject' | 'changes'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [measure, setMeasure] = useState({ value: '', note: '' });
  useEffect(() => {
    if (!initial && id) fetchProposal(id).then(setP).catch((e) => { console.warn('[ProposalCard]', e?.message || e); setP(null); });
  }, [id, initial]);
  if (p === null) return <div className="gos-empty-state"><strong>تعذّر تحميل المقترح</strong></div>;
  if (!p) return <p className="gos-faint">…</p>;

  const act = async (fn) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      setMsg(decisionMessage(r));
      if (r?.ok) { setAsking(null); setReason(''); const fresh = await fetchProposal(p.id); setP(fresh); onChanged?.(fresh); }
    } catch (e) { setMsg(`تعذّر الحفظ: ${e?.message || e}`); }
    setBusy(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(await fetchSummary(p.id)); setMsg('نُسخ الملخص.'); } catch (e) { setMsg(`تعذّر النسخ: ${e?.message || e}`); }
  };
  const pending = p.status === 'pending';
  const result = resultText(p);
  return (
    <div className="gos-card" data-proposal={p.ref} data-status={p.status} style={{ marginBottom: 12 }}>
      <div className="gos-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}><span dir="ltr">{p.ref}</span> — {p.title_ar}</h3>
        <span className={`gos-pill ${STATUS_PILL[p.status] || 'neutral'}`}>{PROPOSAL_STATUS_AR[p.status] || p.status}</span>
        <span className="gos-faint">{KIND_AR[p.kind]}</span>
      </div>
      {p.kind === 'decision' && <p style={{ margin: '4px 0' }} dir="auto">«{p.decision_text}»</p>}
      {p.kind === 'action' && (
        <>
          <Row label="المشكلة">{p.problem}</Row>
          {!compact && <Row label="الدليل">{(p.evidence || []).map((e) => e.figure).filter(Boolean).join(' · ')}</Row>}
          <Row label="الإجراء">{p.action}</Row>
          <Row label="النتيجة المتوقعة">{p.expected_result ? `${p.expected_result} (بحلول ${p.expected_by})` : null}</Row>
          <Row label="التكلفة">{p.cost_sar == null ? null : `${fmt(p.cost_sar)} ر.س`}</Row>
          <Row label="المخاطرة">{p.risk}</Row>
          {p.new_evidence && <Row label="الجديد منذ رفضك">{p.new_evidence}</Row>}
        </>
      )}
      {p.decision_reason && <Row label="سببك">{p.decision_reason}</Row>}
      {result && <p className="gos-card-sub" data-result="1" style={{ margin: '6px 0' }}>{result}</p>}
      <p className="gos-faint" style={{ margin: '6px 0' }}>
        {fmtAt(p.created_at)} · {p.source === 'morning' ? 'من تقرير الصباح' : p.source === 'chat_web' ? 'من المحادثة هنا' : 'من واتساب'}
        {p.decided_at && ` · تقرر ${fmtAt(p.decided_at)}${p.decided_via === 'whatsapp' ? ' من واتساب' : ''}`}
        {expiryText(p) && ` · ${expiryText(p)}`}
      </p>

      {pending && !asking && (
        <div className="gos-srow" style={{ borderTop: 0, flexWrap: 'wrap' }}>
          <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={busy} onClick={() => act(() => decideProposal(p.id, 'approve'))}>وافق</button>
          {!p.kept_at && <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} disabled={busy} onClick={() => act(() => keepProposal(p.id))}>احفظه</button>}
          <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} disabled={busy} onClick={() => setAsking('changes')}>اطلب تعديل</button>
          <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} disabled={busy} onClick={() => setAsking('reject')}>ارفض</button>
        </div>
      )}
      {pending && asking && (
        <div className="gos-srow" style={{ borderTop: 0, flexWrap: 'wrap' }}>
          <input className="gos-sinput note" dir="auto" value={reason} autoFocus aria-label="السبب"
                 placeholder={asking === 'reject' ? 'سبب الرفض (مطلوب)' : 'وش تبي يتغيّر (مطلوب)'} onChange={(e) => setReason(e.target.value)} />
          <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={busy || !reason.trim()}
                  onClick={() => act(() => decideProposal(p.id, asking, reason))}>{asking === 'reject' ? 'ارفض' : 'أرسل التعديل'}</button>
          <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={() => { setAsking(null); setReason(''); }}>إلغاء</button>
        </div>
      )}
      {p.status === 'approved' && p.kind === 'action' && p.expected_metric === 'manual' && p.measured_value == null && !compact && (
        <div className="gos-srow" style={{ borderTop: 0, flexWrap: 'wrap' }}>
          <label className="gos-sfield"><span>النتيجة</span>
            <input className="gos-sinput" type="number" step="any" dir="ltr" value={measure.value} onChange={(e) => setMeasure({ ...measure, value: e.target.value })} /></label>
          <input className="gos-sinput note" dir="auto" placeholder="ملاحظة" aria-label="ملاحظة القياس" value={measure.note} onChange={(e) => setMeasure({ ...measure, note: e.target.value })} />
          <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} disabled={busy || measure.value === ''}
                  onClick={() => act(async () => { const r = await measureManual(p.id, measure.value, measure.note); return r?.ok ? { ok: true, ref: p.ref, status: 'approved', kind: 'action' } : r; })}>سجّل النتيجة</button>
        </div>
      )}
      <div className="gos-srow" style={{ borderTop: 0 }}>
        <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={copy}>انسخ الملخص</button>
        {msg && <span className="gos-faint" data-proposal-msg="1">{msg}</span>}
      </div>
    </div>
  );
}

export default function GrowthApprovals({ user, profile, lang }) {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState(undefined);
  const load = (t = tab) => { setRows(undefined); fetchProposals(t).then(setRows).catch((e) => { console.warn('[GrowthApprovals]', e?.message || e); setRows(null); }); };
  useEffect(() => { load(tab); }, [tab]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="approvals" title="مركز الموافقات">
      <div className="gos-hello">
        <h2>مركز الموافقات</h2>
        <p>كل ما يقترحه المدير ينتظر قرارك هنا وفي واتساب. المقترح لا يتغيّر بعد كتابته؛ التعديل يأتي مقترحاً جديداً. اعتماد قرار يضيفه إلى «القرارات». ما لم تحفظه، يُغلق المقترح المعلّق بعد 14 يوماً.</p>
      </div>
      <div className="gos-rangebar">
        <div className="gos-range" role="tablist">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
      </div>
      {rows === undefined && <p className="gos-faint">…</p>}
      {rows === null && <div className="gos-empty-state"><strong>تعذّر التحميل</strong>أعد تحميل الصفحة.</div>}
      {rows && !rows.length && <div className="gos-empty-state" data-empty={tab}><strong>لا شيء هنا</strong>{tab === 'pending' ? 'لا مقترحات تنتظر قرارك.' : 'لا مقترحات في هذا القسم.'}</div>}
      {rows && rows.map((p) => <ProposalCard key={p.id} p={p} onChanged={() => { if (tab === 'pending') load(tab); }} />)}
    </GrowthShell>
  );
}
